/*
 * NG+ lanyard choice - one product page for the mouthguard with and without a
 * helmet lanyard.
 *
 * Each mode adds exactly ONE cart line, a real product at its own price:
 *   without -> "NeuroGuard+ (Case Included)" variant for the chosen color.
 *              A plain per-color SKU (NGRed, NGBlue, ...).
 *   with    -> "NG+ (Lanyard + Case Included)" bundle variant for the chosen
 *              (mouthguard color, lanyard color). Checkout expands it into the
 *              lanyard-version mouthguard SKU (NGL*, the one with the hole) plus
 *              the L* helmet lanyard SKU.
 * So the SKU mapping is just the variant id; nothing here needs to know about
 * the component SKUs. No discount rules are involved.
 *
 * Markup and the variant maps live in sections/ng-lanyard-choice.liquid. Every
 * query is scoped to this section's [data-ng-lc] root, so the swatch markup it
 * shares with the Double Pack builder (data-ng-dp-* attributes) never crosses
 * over: the Double Pack script only initializes [data-ng-dp] roots.
 */
(function () {
  'use strict';

  function parseJSON(node, fallback) {
    if (!node) return fallback;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return fallback;
    }
  }

  function money(cents) {
    return '$' + ((Number(cents) || 0) / 100).toFixed(2);
  }

  function route(root, attr, fallback) {
    return root.dataset[attr] || (window.routes && window.routes[fallback]) || '/' + fallback.replace('_url', '');
  }

  // Dawn's cart-notification / cart-drawer both expose getSectionsToRender()
  // and renderContents(); reuse them so the add feels like a normal add-to-cart.
  function cartTarget() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function init(root) {
    if (root.dataset.ngLcReady) return;
    root.dataset.ngLcReady = '1';

    var plainVariants = parseJSON(root.querySelector('[data-ng-lc-plain-variants]'), {});
    var withVariants = parseJSON(root.querySelector('[data-ng-lc-with-variants]'), {});
    var colorMedia = parseJSON(root.querySelector('[data-ng-lc-color-media]'), {});
    var hasWith = Object.keys(withVariants).length > 0;

    var hero = root.querySelector('[data-ng-lc-hero]');
    var withHero = root.dataset.ngLcWithHero || '';
    var thumbStrips = Array.prototype.slice.call(root.querySelectorAll('[data-ng-lc-thumbs]'));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-ng-lc-thumb]'));
    var guardGroup = root.querySelector('[data-ng-lc-guard-group]');
    var guardThumb = root.querySelector('[data-ng-lc-guard-thumb]');
    var guardPicked = root.querySelector('[data-ng-lc-guard-picked]');
    var lanyardGroup = root.querySelector('[data-ng-lc-lanyard-group]');
    var lanyardChip = root.querySelector('[data-ng-lc-lanyard-chip]');
    var priceBlock = root.querySelector('[data-ng-lc-price-block]');
    var priceEl = root.querySelector('[data-ng-lc-price]');
    var compareEl = root.querySelector('[data-ng-lc-compare]');
    var salePriceEl = root.querySelector('[data-ng-lc-sale-price]');
    var note = root.querySelector('[data-ng-lc-note]');
    var addButton = root.querySelector('[data-ng-lc-add]');
    var addText = root.querySelector('[data-ng-lc-add-text]');
    var spinner = addButton ? addButton.querySelector('.loading__spinner') : null;
    var status = root.querySelector('[data-ng-lc-status]');
    var appBlocks = root.querySelector('[data-ng-lc-app-blocks]');

    function mode() {
      if (!hasWith) return 'without';
      var input = root.querySelector('[data-ng-lc-mode]:checked');
      return input && input.value === 'with' ? 'with' : 'without';
    }

    // The swatch markup is shared with the Double Pack builder, so the inputs
    // carry data-ng-dp-guard / data-ng-dp-lanyard. Scoped to this root.
    function inputs(scope, role) {
      return Array.prototype.slice.call(scope.querySelectorAll('[data-ng-dp-' + role + ']'));
    }

    function checkedValue(scope, role) {
      var input = scope.querySelector('[data-ng-dp-' + role + ']:checked');
      return input ? input.value : '';
    }

    function setUnavailable(input, unavailable) {
      input.classList.toggle('disabled', unavailable);
    }

    // Grey out any color with no purchasable variant, given the current mode and
    // the other pick. In "with" mode a mouthguard color is judged against the
    // chosen lanyard color and vice versa, since the bundle pairs them.
    function markAvailability(currentMode, guard, lanyard) {
      if (guardGroup) {
        inputs(guardGroup, 'guard').forEach(function (input) {
          var v = currentMode === 'with' ? withVariants[input.value + '|' + lanyard] : plainVariants[input.value];
          setUnavailable(input, !v || !v.available);
        });
      }
      if (lanyardGroup) {
        inputs(lanyardGroup, 'lanyard').forEach(function (input) {
          var v = withVariants[guard + '|' + input.value];
          setUnavailable(input, !v || !v.available);
        });
      }
    }

    // Show one image in the hero and mark the thumbnail it came from, if the
    // current strip happens to contain it. Colors whose photo is not in the
    // strip (or is shared by two colors) simply leave nothing highlighted.
    var swapTimer = null;
    function showImage(url) {
      if (!hero || !url) return;
      if (hero.getAttribute('src') !== url) {
        hero.setAttribute('data-ng-lc-swapping', '');
        hero.setAttribute('src', url);
        clearTimeout(swapTimer);
        swapTimer = setTimeout(function () {
          hero.removeAttribute('data-ng-lc-swapping');
        }, 120);
      }
      thumbs.forEach(function (thumb) {
        if (thumb.dataset.ngLcFull === url) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
    }

    // The photo for a color in a given mode: the mode's own product shot, so
    // With lanyard shows that color photographed with the lanyard on it.
    function colorImage(currentMode, color, size) {
      var forMode = colorMedia[currentMode] || {};
      var entry = forMode[color];
      if (entry && entry[size]) return entry[size];
      // A color the bundle has no photo for falls back to the plain shot, then
      // to the section's image setting for that mode.
      var plainEntry = (colorMedia.without || {})[color];
      if (plainEntry && plainEntry[size]) return plainEntry[size];
      if (size !== 'full' || !hero) return '';
      return currentMode === 'with' ? hero.dataset.ngLcFallbackWith : hero.dataset.ngLcFallbackWithout;
    }

    // `trigger` is 'color' when a swatch moved, otherwise a mode change or first
    // load. Entering With lanyard opens on the lanyard product shot - the
    // bundle's per-color photos are mostly duplicates of the plain ones and
    // would not show the lanyard - while a later swatch click still maps the
    // hero to that color.
    function updateMedia(currentMode, color, trigger) {
      thumbStrips.forEach(function (strip) {
        strip.hidden = strip.dataset.ngLcThumbs !== currentMode;
      });
      // 'validate' re-runs the pricing/availability pass without touching the
      // gallery, so clicking Add to cart never moves the image the shopper
      // chose. Only a swatch or a mode change re-picks the hero.
      if (trigger === 'validate') return;
      if (trigger !== 'color' && currentMode === 'with' && withHero) {
        showImage(withHero);
        return;
      }
      showImage(colorImage(currentMode, color, 'full'));
    }

    /* ---------------- Shopify Subscriptions widget ----------------
     * The app block renders one <section data-variant-id> per variant, each
     * with a "One-time purchase" radio and a radio per selling plan. Its own
     * script drives this from Dawn's variant picker and product form, which
     * this section does not have, so we do it here: show the section for the
     * selected variant, and read the chosen plan at add-to-cart time.
     *
     * Which variants get a widget is read from the DOM, not hard-coded: the app
     * renders a block only for variants that actually have a selling plan, so
     * the widget shows for any variant it covers and hides for any it does not.
     * Today that means it appears in Without lanyard mode and not in With,
     * because only the plain mouthguard has plans - but if the lanyard bundle
     * is ever given plans and its own app block, this starts working with no
     * code change, and a Subscribe option can never appear for a variant that
     * checkout would ignore.
     */
    // Only the per-variant <section> elements - the one-time radio inside each
    // one carries data-variant-id too, so match on the tag as well.
    var SUBSCRIPTION_HIDDEN_CLASS = 'shopify_subscriptions_app_block--hidden';

    function subscriptionSections() {
      if (!appBlocks) return [];
      return Array.prototype.slice.call(appBlocks.querySelectorAll('section[data-variant-id]'));
    }

    function subscriptionSectionFor(variant) {
      if (!variant) return null;
      return (
        subscriptionSections().filter(function (section) {
          return String(section.dataset.variantId) === String(variant.id);
        })[0] || null
      );
    }

    function syncSubscriptions(variant) {
      if (!appBlocks) return;
      var sections = subscriptionSections();
      // Show the widget only when this exact variant has a block of its own.
      var show = !!subscriptionSectionFor(variant);
      appBlocks.hidden = !show;
      if (!sections.length) return;
      sections.forEach(function (section) {
        var isCurrent = show && String(section.dataset.variantId) === String(variant.id);
        // The app hides its non-current variants with BOTH the hidden attribute
        // and its own class, and it renders the first variant visible. Set both
        // so our selection wins over what the app shipped.
        section.hidden = !isCurrent;
        section.classList.toggle(SUBSCRIPTION_HIDDEN_CLASS, !isCurrent);
        if (!isCurrent) return;
        // Never leave the visible section with nothing chosen.
        if (!section.querySelector('input[type="radio"]:checked')) {
          var oneTime = section.querySelector('[data-radio-type="one_time_purchase"]');
          if (oneTime) oneTime.checked = true;
        }
      });
    }

    // The selling plan the shopper picked for this variant, or '' for one-time.
    function selectedSellingPlan(variant) {
      if (!appBlocks || appBlocks.hidden || !variant) return '';
      var section = subscriptionSectionFor(variant);
      if (!section) return '';
      var checked = section.querySelector('input[type="radio"]:checked');
      return (checked && checked.dataset.sellingPlanId) || '';
    }

    // When a plan is chosen the widget knows the subscription price; reuse the
    // string Shopify already formatted rather than recomputing the discount.
    function sellingPlanPrice(variant) {
      if (!appBlocks || appBlocks.hidden || !variant) return '';
      var section = subscriptionSectionFor(variant);
      if (!section) return '';
      var checked = section.querySelector('input[type="radio"]:checked');
      if (!checked || !checked.dataset.sellingPlanId) return '';
      // "$44.97 USD" -> "$44.97", to match the rest of the page.
      return (checked.dataset.variantPrice || '').replace(/\s*[A-Z]{3}\s*$/, '').trim();
    }

    function update(trigger) {
      var currentMode = mode();
      if (lanyardGroup) lanyardGroup.hidden = currentMode !== 'with';

      var guard = guardGroup ? checkedValue(guardGroup, 'guard') : '';
      var lanyard = lanyardGroup ? checkedValue(lanyardGroup, 'lanyard') : '';

      updateMedia(currentMode, guard, trigger);

      var cardImage = colorImage(currentMode, guard, 'card');
      if (guardThumb && cardImage) {
        guardThumb.src = cardImage;
        guardThumb.alt = guard + ' NeuroGuard+';
      }
      if (guardPicked) guardPicked.textContent = guard ? guard + ' mouthguard' : '';
      if (lanyardChip) {
        var lanyardInput = lanyardGroup && lanyardGroup.querySelector('[data-ng-dp-lanyard]:checked');
        lanyardChip.style.setProperty('--ng-dp-swatch', (lanyardInput && lanyardInput.dataset.ngDpColor) || '#1f1f21');
      }
      markAvailability(currentMode, guard, lanyard);

      var problem = '';
      var variant = currentMode === 'with' ? withVariants[guard + '|' + lanyard] : plainVariants[guard];
      if (!variant) {
        problem = currentMode === 'with' ? 'That mouthguard and lanyard pairing is not offered.' : 'That color is not offered.';
      } else if (!variant.available) {
        problem = currentMode === 'with' ? guard + ' with a ' + lanyard + ' lanyard is sold out right now.' : guard + ' is sold out right now.';
      }

      syncSubscriptions(problem ? null : variant);

      var total = variant ? variant.price : 0;
      var compare = variant && variant.compare > variant.price ? variant.compare : total;
      var onSale = !problem && compare > total;

      // A chosen subscription shows the plan's price, so the page never quotes
      // the one-time price for a subscription the shopper is about to buy.
      var planPrice = sellingPlanPrice(problem ? null : variant);
      var shownTotal = planPrice || money(total);

      if (priceBlock) priceBlock.classList.toggle('price--on-sale', onSale && !planPrice);
      if (priceEl) priceEl.textContent = shownTotal;
      if (compareEl) compareEl.textContent = money(compare);
      if (salePriceEl) salePriceEl.textContent = shownTotal;

      if (note) {
        note.textContent = problem;
        note.hidden = !problem;
      }
      if (addButton) {
        addButton.disabled = !!problem;
        if (addText) addText.textContent = problem ? 'Choose an available combination' : 'Add to cart — ' + shownTotal;
      }

      return {
        mode: currentMode,
        variant: problem ? null : variant,
        problem: problem,
        sellingPlan: selectedSellingPlan(problem ? null : variant),
      };
    }

    function setBusy(busy) {
      if (!addButton) return;
      addButton.setAttribute('aria-disabled', busy ? 'true' : 'false');
      addButton.classList.toggle('loading', busy);
      if (spinner) spinner.classList.toggle('hidden', !busy);
    }

    function showStatus(message, isError) {
      if (!status) return;
      status.textContent = message;
      status.classList.toggle('ng-dp__status--error', !!isError);
    }

    function add() {
      if (!addButton || addButton.getAttribute('aria-disabled') === 'true') return;
      var state = update('validate');
      if (state.problem || !state.variant) return;

      var target = cartTarget();
      var item = { id: state.variant.id, quantity: 1 };
      if (state.sellingPlan) item.selling_plan = Number(state.sellingPlan) || state.sellingPlan;
      var body = { items: [item] };
      if (target && typeof target.getSectionsToRender === 'function') {
        body.sections = target
          .getSectionsToRender()
          .map(function (section) {
            return section.id;
          })
          .join(',');
        body.sections_url = window.location.pathname;
      }

      setBusy(true);
      showStatus('Adding to your cart…', false);

      fetch(route(root, 'ngLcAddUrl', 'cart_add_url'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (response) {
          if (response.status && response.status !== 200) {
            throw new Error(response.description || response.message || 'Could not add to cart.');
          }

          showStatus(
            state.sellingPlan
              ? '✓ Subscription added to your cart.'
              : state.mode === 'with'
              ? '✓ Mouthguard and lanyard added to your cart.'
              : '✓ Mouthguard added to your cart.',
            false
          );

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ng-lanyard-choice', cartData: response });
          }

          var first = (response.items || [])[0];
          if (target && typeof target.renderContents === 'function' && response.sections && first) {
            // Dawn keys the notification off `key` (notification) / `id` (drawer).
            target.renderContents({ key: first.key, id: first.id, sections: response.sections });
            if (typeof target.setActiveElement === 'function') target.setActiveElement(addButton);
          } else {
            window.location.href = route(root, 'ngLcCartUrl', 'cart_url');
          }
        })
        .catch(function (error) {
          showStatus(error.message || 'Could not add to cart. Please try again.', true);
        })
        .then(function () {
          setBusy(false);
        });
    }

    root.addEventListener('change', function (event) {
      if (event.target.matches('[data-ng-dp-guard], [data-ng-dp-lanyard]')) {
        update('color');
      } else if (event.target.matches('[data-ng-lc-mode]')) {
        update('mode');
      } else if (appBlocks && appBlocks.contains(event.target) && event.target.type === 'radio') {
        // One-time vs a subscription plan: price and button text only.
        update('validate');
      }
    });
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        showImage(thumb.dataset.ngLcFull);
      });
    });
    if (addButton) addButton.addEventListener('click', add);

    update();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-ng-lc]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Theme editor re-renders the section on setting changes.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
